// Deepgram nova-2 real-time streaming dictation engine.
//
// Adapted from imaSpark's Deepgram streaming (same nova-2 params + interim/final
// handling), re-shaped for VERA's serverless architecture:
//   • imaSpark proxies audio through a persistent Node WS server that holds the key.
//   • VERA has no such server, so a Cloud Function (getDeepgramToken) mints a
//     ~5-min token and the browser streams DIRECTLY to Deepgram. The master key
//     stays in Secret Manager; only a short-lived token reaches the client.
//
// iOS/Android WKWebView-safe: uses a browser WebSocket + Web Audio (getUserMedia
// → ScriptProcessor → linear16 PCM @ 16 kHz). We deliberately do NOT use
// MediaRecorder for the stream — iOS Safari's MediaRecorder emits mp4/aac, which
// does not stream cleanly to Deepgram; raw PCM behaves identically everywhere.
//
// Long-session robustness (a 20-minute clinical note outlives one token):
//   • Proactive refresh ~15s before token expiry.
//   • Reactive reconnect on any drop (network, Deepgram idle-close, expiry) with
//     exponential backoff.
//   • Audio is buffered during the sub-second reconnect gap so no speech is lost.
//   • KeepAlive during silence/privacy so Deepgram doesn't close an idle socket.
// Finalized transcript already emitted is preserved by the UI, so reconnects are
// invisible beyond a brief "reconnecting…" status.

import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  DictationCallbacks,
  DictationEngine,
  DictationStartOptions,
} from './types';
import { appendKeyterms } from './abaKeyterms';

const TARGET_SR = 16000;

// Deepgram model. A/B target for max clinical accuracy: 'nova-3-medical' or
// 'nova-2-medical' (confirm streaming support + BAA coverage first). The keyterm
// param auto-switches (keyterm for nova-3, keywords for nova-2).
const DEEPGRAM_MODEL = 'nova-2';
const KEEPALIVE_MS = 8000; // Deepgram closes idle sockets after ~10-12s
const MAX_QUEUE_SAMPLES = TARGET_SR * 10; // cap the reconnect audio buffer at ~10s
const MAX_RECONNECT_ATTEMPTS = 8;

// Deepgram live params — mirrors imaSpark, plus mip_opt_out (model-improvement
// opt-out: our audio is not used to train Deepgram's models) and keyterm biasing
// (ABA glossary + the session's client name) — the biggest clinical-accuracy lever.
function buildDeepgramUrl(language: string, dynamicKeyterms: string[]): string {
  const p = new URLSearchParams({
    model: DEEPGRAM_MODEL,
    language,
    interim_results: 'true',
    smart_format: 'true',
    punctuate: 'true',
    endpointing: '300',
    encoding: 'linear16',
    sample_rate: String(TARGET_SR),
    channels: '1',
    mip_opt_out: 'true',
  });
  appendKeyterms(p, DEEPGRAM_MODEL, dynamicKeyterms);
  return `wss://api.deepgram.com/v1/listen?${p.toString()}`;
}

// Browsers can't set WebSocket headers, so Deepgram credentials ride the
// Sec-WebSocket-Protocol subprotocol. Deepgram documents API keys as
// ['token', key]; a grant/bearer token's exact subprotocol keyword isn't pinned
// down in the docs, so we try 'bearer' first and AUTO-FALL-BACK to 'token' if the
// handshake is rejected before the socket opens (see openConnection). Whichever
// form connects is then locked in for the session.
const AUTH_FORMS = ['bearer', 'token'] as const;
function authSubprotocol(form: string, token: string): string[] {
  return [form, token];
}

export class DeepgramDictationEngine implements DictationEngine {
  readonly provider = 'deepgram' as const;
  private cb: DictationCallbacks;
  private language = 'en-US';
  private dynamicKeyterms: string[] = []; // per-session terms (client name)

  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private mute: GainNode | null = null;
  private stream: MediaStream | null = null;
  private ownedStream = false;

  private ws: WebSocket | null = null;
  private closed = false;      // stop() called — never reconnect
  private running = false;     // between start() and stop()
  private paused = false;      // privacy pause — keep socket, stop sending audio
  private everConnected = false;
  private reconnectAttempts = 0;
  private authFormIdx = 0; // which AUTH_FORMS entry to use (advances on handshake reject)

  private queue: Int16Array[] = [];
  private queueSamples = 0;

  private keepAliveTimer: number | null = null;
  private refreshTimer: number | null = null;
  private reconnectTimer: number | null = null;

  constructor(callbacks: DictationCallbacks) {
    this.cb = callbacks;
  }

  isSupported(): boolean {
    return (
      typeof WebSocket !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      !!((window as any).AudioContext || (window as any).webkitAudioContext)
    );
  }

  async start(opts?: DictationStartOptions): Promise<void> {
    if (opts?.language) this.language = opts.language;
    this.dynamicKeyterms = opts?.keyterms ?? [];
    this.closed = false;
    this.running = true;
    this.paused = false;
    this.everConnected = false;
    this.reconnectAttempts = 0;

    // 1. Mic — reuse DictateNotes' shared stream, or open our own.
    this.ownedStream = !opts?.stream;
    this.stream =
      opts?.stream ??
      (await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      }));

    // 2. Web Audio graph → PCM pump.
    const AC: typeof AudioContext =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    try {
      this.ctx = new AC({ sampleRate: TARGET_SR });
    } catch {
      this.ctx = new AC();
    }
    this.source = this.ctx.createMediaStreamSource(this.stream);
    this.processor = this.ctx.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (e) => this.onAudio(e);
    // ScriptProcessor needs a live downstream node to fire — pipe to a muted gain.
    this.mute = this.ctx.createGain();
    this.mute.gain.value = 0;
    this.source.connect(this.processor);
    this.processor.connect(this.mute);
    this.mute.connect(this.ctx.destination);
    await this.ctx.resume();

    // 3. Open the Deepgram connection.
    await this.openConnection();
  }

  pause(): void {
    this.paused = true; // stop feeding audio; KeepAlive holds the socket open
  }

  resume(): void {
    this.paused = false;
  }

  async stop(): Promise<void> {
    this.closed = true;
    this.running = false;
    this.clearTimers();
    // Ask Deepgram to finalize, then drop the socket.
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'CloseStream' }));
      } catch {
        /* noop */
      }
    }
    this.teardownSocket();
    // Tear down audio graph.
    try { this.processor?.disconnect(); } catch { /* noop */ }
    try { this.source?.disconnect(); } catch { /* noop */ }
    try { this.mute?.disconnect(); } catch { /* noop */ }
    try { await this.ctx?.close(); } catch { /* noop */ }
    this.processor = null;
    this.source = null;
    this.mute = null;
    this.ctx = null;
    if (this.ownedStream) this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.queue = [];
    this.queueSamples = 0;
    this.cb.onStatus?.('stopped');
  }

  // ── Audio pump ──────────────────────────────────────────────────────
  private onAudio(e: AudioProcessingEvent) {
    if (!this.running || this.paused || this.closed) return;
    const input = e.inputBuffer.getChannelData(0); // Float32 @ ctx.sampleRate
    const pcm = this.encodeChunk(input);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(pcm.buffer);
      } catch {
        /* the close/reconnect path will handle it */
      }
    } else {
      // Buffer during a reconnect so no speech is lost; bound the memory.
      this.queue.push(pcm);
      this.queueSamples += pcm.length;
      while (this.queueSamples > MAX_QUEUE_SAMPLES && this.queue.length) {
        const dropped = this.queue.shift()!;
        this.queueSamples -= dropped.length;
      }
    }
  }

  private encodeChunk(input: Float32Array): Int16Array {
    const inRate = this.ctx?.sampleRate ?? TARGET_SR;
    const mono = inRate === TARGET_SR ? input : downsample(input, inRate, TARGET_SR);
    const out = new Int16Array(mono.length);
    for (let i = 0; i < mono.length; i++) {
      const s = Math.max(-1, Math.min(1, mono[i]));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }

  private flushQueue() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    for (const chunk of this.queue) {
      try {
        this.ws.send(chunk.buffer);
      } catch {
        break;
      }
    }
    this.queue = [];
    this.queueSamples = 0;
  }

  // ── Connection lifecycle (token mint + refresh + reconnect) ─────────
  private async fetchToken(): Promise<{ token: string; expiresInSeconds: number }> {
    const call = httpsCallable(getFunctions(), 'getDeepgramToken');
    const res: any = await call({});
    if (!res?.data?.success || !res?.data?.token) {
      throw new Error(res?.data?.error || 'Could not obtain a streaming token.');
    }
    return {
      token: res.data.token,
      expiresInSeconds: res.data.expiresInSeconds ?? 300,
    };
  }

  private async openConnection(): Promise<void> {
    if (this.closed) return;
    this.clearTimers();
    this.teardownSocket(); // supersede any prior socket (proactive refresh)
    this.cb.onStatus?.(this.everConnected ? 'reconnecting' : 'connecting');
    try {
      const { token, expiresInSeconds } = await this.fetchToken();
      if (this.closed) return;
      let opened = false;
      const ws = new WebSocket(
        buildDeepgramUrl(this.language, this.dynamicKeyterms),
        authSubprotocol(AUTH_FORMS[this.authFormIdx], token)
      );
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      ws.onopen = () => {
        if (this.ws !== ws) return;
        opened = true; // this auth form works — it stays locked for the session
        this.everConnected = true;
        this.reconnectAttempts = 0;
        this.cb.onStatus?.('live');
        this.flushQueue();
        this.startKeepAlive();
        // Proactive refresh a bit before the token lapses.
        const refreshMs = Math.max(5000, (expiresInSeconds - 15) * 1000);
        this.refreshTimer = window.setTimeout(() => this.openConnection(), refreshMs);
      };
      ws.onmessage = (ev) => this.handleMessage(ev);
      ws.onerror = () => { /* onclose follows */ };
      ws.onclose = () => {
        if (this.closed || this.ws !== ws) return; // intentional / superseded
        // Rejected before it ever opened, with an untried auth form left → the
        // subprotocol keyword was likely wrong; switch form and retry at once
        // (not counted as a reconnect attempt).
        if (!opened && this.authFormIdx < AUTH_FORMS.length - 1) {
          this.authFormIdx++;
          void this.openConnection();
          return;
        }
        this.scheduleReconnect();
      };
    } catch {
      if (this.closed) return;
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.closed) return;
    this.teardownSocket();
    this.stopKeepAlive();
    this.reconnectAttempts++;
    if (this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      this.cb.onError?.('Lost connection to the dictation service. Your transcript so far is preserved.');
      this.cb.onStatus?.('error');
      return;
    }
    const delay = Math.min(300 * 2 ** (this.reconnectAttempts - 1), 4000);
    this.cb.onStatus?.('reconnecting');
    this.reconnectTimer = window.setTimeout(() => this.openConnection(), delay);
  }

  private handleMessage(ev: MessageEvent) {
    let msg: any;
    try {
      msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
    } catch {
      return;
    }
    if (!msg) return;
    if (msg.type === 'Results' || msg.channel) {
      const text: string | undefined = msg.channel?.alternatives?.[0]?.transcript?.trim();
      if (text) {
        if (msg.is_final) {
          this.cb.onFinal(text);
          this.cb.onInterim('');
        } else {
          this.cb.onInterim(text);
        }
      }
    }
    // Metadata / UtteranceEnd / SpeechStarted: not needed for VERA's UX.
  }

  private startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveTimer = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'KeepAlive' }));
        } catch {
          /* noop */
        }
      }
    }, KEEPALIVE_MS);
  }

  private stopKeepAlive() {
    if (this.keepAliveTimer !== null) {
      window.clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  private clearTimers() {
    this.stopKeepAlive();
    if (this.refreshTimer !== null) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private teardownSocket() {
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    } catch {
      /* noop */
    }
  }
}

/** Linear-interpolation downsampler (cheap, dependency-free). */
function downsample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate <= toRate) return input;
  const ratio = fromRate / toRate;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const pos = i * ratio;
    const lo = Math.floor(pos);
    const hi = Math.min(lo + 1, input.length - 1);
    const frac = pos - lo;
    out[i] = input[lo] * (1 - frac) + input[hi] * frac;
  }
  return out;
}
